<template>
  <div class="employees-page">
    <!-- 로딩 컴포넌트 -->
    <LodingComponent :is-visible="isLoading" :text="loadingText" />
    
    <!-- 페이지 헤더 -->
    <div class="page-header">
      <div class="header-left">
        <div class="page-title-wrapper">
          <h1 class="page-title">사원 관리</h1>
          <span class="active-count-chip">재직 중 {{ employeesSummary.active }}명</span>
        </div>
        <p class="page-subtitle">기관 소속 사원 정보를 관리하세요</p>
      </div>
      <div class="header-right">
        <v-menu>
          <template v-slot:activator="{ props }">
            <v-btn
              color="#1e3a8a"
              variant="outlined"
              prepend-icon="mdi-file-download"
              v-bind="props"
              class="mr-2"
            >
              자료 다운로드
            </v-btn>
          </template>
          <v-list density="compact" class="download-menu">
            <v-list-item
              v-for="file in downloadableFiles"
              :key="file.name"
              @click="downloadFile(file)"
            >
              <template v-slot:prepend>
                <v-icon size="20" color="#1e3a8a">mdi-file-document</v-icon>
              </template>
              <v-list-item-title>{{ file.label }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-btn
          color="#1e3a8a"
          variant="outlined"
          prepend-icon="mdi-account-key"
          @click="openBulkCredentialDialog"
          class="mr-2"
        >
          계정부여
        </v-btn>
        <v-btn
          color="#1e3a8a"
          variant="outlined"
          prepend-icon="mdi-account-group"
          @click="openMemberUploadDialog"
          class="mr-2"
        >
          사원정보 업로드
        </v-btn>
        <v-btn
          color="#059669"
          variant="outlined"
          prepend-icon="mdi-calendar"
          @click="openScheduleUploadDialog"
          class="mr-2"
        >
          일정계획 업로드
        </v-btn>
        <v-btn
          color="#1e3a8a"
          variant="flat"
          prepend-icon="mdi-account-plus"
          @click="openEmployeeDialog(null)"
        >
          신규 등록
        </v-btn>
      </div>
    </div>

    <!-- 검색 및 필터 -->
    <v-card class="filter-card" elevation="1">
      <v-card-text>
        <div class="filter-row">
          <v-text-field
            v-model="filters.keyword"
            variant="outlined"
            density="compact"
            prepend-inner-icon="mdi-magnify"
            placeholder="이름, 사원코드, 연락처"
            clearable
            hide-details
            class="filter-input"
          ></v-text-field>
          
          <v-select
            v-model="filters.job"
            :items="jobOptions"
            variant="outlined"
            density="compact"
            placeholder="직종 선택"
            clearable
            hide-details
            class="filter-input"
          ></v-select>
          
          <v-select
            v-model="filters.status"
            :items="statusOptions"
            item-title="label"
            item-value="value"
            variant="outlined"
            density="compact"
            placeholder="재직 상태"
            clearable
            hide-details
            class="filter-input"
          ></v-select>
          
          <v-btn
            variant="text"
            @click="resetFilters"
            class="reset-btn"
          >
            <v-icon size="18" class="mr-1">mdi-refresh</v-icon>
            초기화
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- 사원 목록 테이블 -->
    <v-card class="table-card" elevation="1">
      <v-data-table
        :headers="headers"
        :items="filteredEmployees"
        :loading="isLoading"
        :items-per-page="-1"
        hide-default-footer
        class="employees-table"
        density="comfortable"
      >
        <template #item.emp_name="{ item }">
          <div class="employee-name-cell">
            <v-avatar size="32" color="#1e3a8a" class="mr-2">
              <span class="avatar-text">{{ item.emp_name.charAt(0) }}</span>
            </v-avatar>
            <span class="employee-name">{{ item.emp_name }}</span>
          </div>
        </template>
        
        <template #item.emp_id="{ item }">
          <span :class="{ 'no-id': !item.emp_id }">{{ item.emp_id || '-' }}</span>
        </template>
        
        <template #item.emp_birth="{ item }">
          <span>{{ formatBirth(item.emp_birth) }}</span>
        </template>
        
        <template #item.emp_phone="{ item }">
          <span>{{ item.emp_phone || '-' }}</span>
        </template>
        
        <template #item.emp_in_date="{ item }">
          <span>{{ item.emp_in_date || '-' }}</span>
        </template>
        
        <template #item.emp_out_date="{ item }">
          <span>{{ item.emp_out_date || '-' }}</span>
        </template>
        
        <template #item.documents="{ item }">
          <div class="document-status-cell" @click="openDocumentDialog(item)">
            <!-- 사직서가 있는 경우 -->
            <template v-if="(item.has_resignation ?? 0) > 0">
              <div class="resignation-indicator" :class="item.resignation_status || ''">
                <v-icon size="16" color="white">mdi-file-remove</v-icon>
                <span>{{ getResignationBadgeText(item.resignation_status || '') }}</span>
              </div>
            </template>
            <!-- 다른 문서만 있는 경우 -->
            <template v-else-if="(item.doc_count ?? 0) > 0">
              <div class="doc-indicator">
                <v-icon size="16" color="#1e3a8a">mdi-file-document</v-icon>
                <span>{{ item.doc_count }}건</span>
              </div>
            </template>
            <!-- 문서 없음 -->
            <template v-else>
              <div class="no-doc-indicator">
                <v-icon size="16" color="#9ca3af">mdi-file-document-outline</v-icon>
                <span>-</span>
              </div>
            </template>
            <v-tooltip activator="parent" location="top">
              <template v-if="(item.has_resignation ?? 0) > 0">
                📄 사직서 {{ getDocStatusText(item.resignation_status || '') }}<br>
                클릭하여 상세 보기
              </template>
              <template v-else-if="(item.doc_count ?? 0) > 0">
                문서 {{ item.doc_count }}건<br>
                클릭하여 상세 보기
              </template>
              <template v-else>
                제출된 문서 없음
              </template>
            </v-tooltip>
          </div>
        </template>
        
        <template #item.actions="{ item }">
          <div class="action-buttons">
            <v-btn
              icon
              size="small"
              variant="text"
              @click="viewEmployeeDetail(item)"
            >
              <v-icon size="18">mdi-information</v-icon>
              <v-tooltip activator="parent" location="top">상세정보</v-tooltip>
            </v-btn>
            <v-btn
              icon
              size="small"
              variant="text"
              @click="openScheduleCalendar(item)"
            >
              <v-icon size="18">mdi-calendar-clock</v-icon>
              <v-tooltip activator="parent" location="top">일정계획</v-tooltip>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- 사원 상세 모달 -->
    <v-dialog v-model="detailDialog" max-width="700px" >
      <v-card class="detail-modal-card">
        <v-card-title class="detail-header">
          <div class="header-content">
            <v-avatar size="48" color="white" class="mr-3">
              <span class="avatar-initial">{{ selectedEmployee?.emp_name?.charAt(0) }}</span>
            </v-avatar>
            <div class="header-info">
              <h2>{{ selectedEmployee?.emp_name }}</h2>
              <span class="job-badge">{{ selectedEmployee?.emp_job }}</span>
            </div>
          </div>
        </v-card-title>
        
        <v-card-text class="detail-content" v-if="selectedEmployee">
          <div v-if="!isEditMode" class="detail-sections">
            <!-- 기본 정보 섹션 -->
            <div class="detail-section">
              <div class="section-header">
                <v-icon size="20" color="#1e3a8a">mdi-account</v-icon>
                <span>기본 정보</span>
              </div>
              <div class="info-table">
                <div class="info-row">
                  <div class="info-label">이름</div>
                  <div class="info-value">{{ selectedEmployee.emp_name }}</div>
                </div>
                <div class="info-row">
                  <div class="info-label">생년월일</div>
                  <div class="info-value">{{ formatBirth(selectedEmployee.emp_birth) }}</div>
                </div>
                <div class="info-row">
                  <div class="info-label">주민등록번호</div>
                  <div class="info-value birth-number-value">
                    <template v-if="selectedEmployee.has_birth_number">
                      <span v-if="birthNumberState.visible" class="ssn-value">{{ formatBirthNumber(birthNumberState.value, selectedEmployee.emp_birth) }}</span>
                      <span v-else class="ssn-masked">{{ getBirthNumberMaskedText() }}</span>
                      <v-btn
                        v-if="selectedEmployee?.emp_idx"
                        class="reveal-btn"
                        size="x-small"
                        color="#1e3a8a"
                        variant="tonal"
                        :loading="birthNumberState.loading"
                        @click="toggleBirthNumberVisibility"
                      >
                        {{ birthNumberState.visible ? '숨기기' : '보기' }}
                      </v-btn>
                    </template>
                    <span v-else class="not-registered">미등록</span>
                  </div>
                </div>
                <div class="info-row">
                  <div class="info-label">직종</div>
                  <div class="info-value">
                    <span class="job-chip">{{ selectedEmployee.emp_job }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 연락처 정보 섹션 -->
            <div class="detail-section">
              <div class="section-header">
                <v-icon size="20" color="#1e3a8a">mdi-phone</v-icon>
                <span>연락처 정보</span>
              </div>
              <div class="info-table">
                <div class="info-row">
                  <div class="info-label">휴대폰번호</div>
                  <div class="info-value">
                    <span v-if="selectedEmployee.emp_phone" class="phone-value">{{ selectedEmployee.emp_phone }}</span>
                    <span v-else class="not-registered">-</span>
                  </div>
                </div>
                <div class="info-row">
                  <div class="info-label">이메일</div>
                  <div class="info-value">
                    <span v-if="selectedEmployee.emp_email" class="email-value">{{ selectedEmployee.emp_email }}</span>
                    <span v-else class="not-registered">-</span>
                  </div>
                </div>
                <div class="info-row">
                  <div class="info-label">주소</div>
                  <div class="info-value">
                    <span v-if="selectedEmployee.emp_addr">
                      {{ selectedEmployee.emp_addr }}
                      {{ selectedEmployee.emp_addr_detail ? ` ${selectedEmployee.emp_addr_detail}` : '' }}
                    </span>
                    <span v-else class="not-registered">-</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 재직 정보 섹션 -->
            <div class="detail-section">
              <div class="section-header">
                <v-icon size="20" color="#1e3a8a">mdi-briefcase</v-icon>
                <span>재직 정보</span>
              </div>
              <div class="info-table">
                <div class="info-row">
                  <div class="info-label">입사일</div>
                  <div class="info-value">
                    <span v-if="selectedEmployee.emp_in_date" class="date-value">{{ selectedEmployee.emp_in_date }}</span>
                    <span v-else class="not-registered">-</span>
                  </div>
                </div>
                <div class="info-row">
                  <div class="info-label">퇴사일</div>
                  <div class="info-value">
                    <span v-if="selectedEmployee.emp_out_date" class="date-value resigned">{{ selectedEmployee.emp_out_date }}</span>
                    <span v-else class="active-status">재직 중</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 계정 정보 섹션 -->
            <div class="detail-section account-section">
              <div class="section-header">
                <v-icon size="20" color="#1e3a8a">mdi-account-key</v-icon>
                <span>계정 정보</span>
              </div>
              <div class="account-info-box">
                <template v-if="credentialState.hasAccount">
                  <div class="account-status-card registered">
                    <v-icon size="28" color="#16a34a">mdi-check-circle</v-icon>
                    <div class="account-details">
                      <span class="status-text">계정 등록됨</span>
                      <span class="account-id">ID: {{ credentialState.empId }}</span>
                    </div>
                    <v-btn
                      size="small"
                      color="#1e3a8a"
                      variant="tonal"
                      @click="openCredentialDialog"
                    >
                      계정 수정
                    </v-btn>
                  </div>
                </template>
                <template v-else>
                  <div class="account-status-card unregistered">
                    <v-icon size="28" color="#9ca3af">mdi-account-off</v-icon>
                    <div class="account-details">
                      <span class="status-text">계정 미등록</span>
                      <span class="status-desc">앱 로그인이 불가합니다</span>
                    </div>
                    <v-btn
                      size="small"
                      color="#1e3a8a"
                      variant="flat"
                      @click="openCredentialDialog"
                    >
                      <v-icon size="16" class="mr-1">mdi-account-key</v-icon>
                      계정 부여
                    </v-btn>
                  </div>
                </template>
              </div>
            </div>
          </div>
          
          <!-- 수정 모드 -->
          <div v-else class="edit-form">
            <div class="form-section">
              <h4 class="section-title">기본 정보</h4>
              
              <div class="compact-field read-only-field">
                <label class="compact-label">이름 *</label>
                <div class="read-only-value">{{ editFormData.emp_name || '-' }}</div>
              </div>
              
              <div class="compact-field read-only-field">
                <label class="compact-label">생년월일 *</label>
                <div class="read-only-value">{{ formatBirth(editFormData.emp_birth) || '-' }}</div>
              </div>
              
              <div class="compact-field">
                <label class="compact-label">주민등록번호</label>
                <div class="birth-number-input-group">
                  <input
                    v-model="birthNumberFront"
                    type="text"
                    class="compact-input birth-front"
                    maxlength="6"
                    placeholder="앞자리 6자리"
                    @input="onEditBirthNumberFrontInput"
                  />
                  <span class="birth-separator">-</span>
                  <input
                    v-model="birthNumberBack"
                    type="text"
                    class="compact-input birth-back"
                    maxlength="7"
                    placeholder="뒷자리 7자리"
                    @input="onEditBirthNumberBackInput"
                  />
                </div>
                <div class="field-hint">
                  현재 상태: {{ getBirthNumberStatusText() }}.
                  <v-btn
                    v-if="editFormData.emp_birth"
                    size="x-small"
                    variant="text"
                    color="#1e3a8a"
                    class="ml-1"
                    @click="fillBirthFrontFromBirth"
                  >
                    생년월일에서 앞자리 채우기
                  </v-btn>
                </div>
                <div
                  class="field-action"
                  v-if="hasBirthNumberRegistered"
                >
                  <v-btn
                    size="small"
                    variant="text"
                    color="#1e3a8a"
                    :loading="birthNumberState.loading"
                    @click="fillBirthNumberForEdit"
                  >
                    기존 값 불러오기
                  </v-btn>
                </div>
              </div>
              
              <div class="compact-field">
                <label class="compact-label">직종 *</label>
                <v-select
                  v-model="editFormData.emp_job"
                  :items="jobOptions"
                  variant="outlined"
                  density="compact"
                  placeholder="직종을 선택하세요"
                  hide-details
                  :menu-props="{ zIndex: 9999 }"
                ></v-select>
              </div>
            </div>

            <div class="form-section">
              <h4 class="section-title">연락처 정보</h4>
              
              <div class="compact-field">
                <label class="compact-label">휴대폰번호</label>
                <input
                  v-model="editFormData.emp_phone"
                  type="text"
                  class="compact-input"
                  maxlength="13"
                  @input="onEditPhoneInput"
                />
              </div>
              
              <div class="compact-field">
                <label class="compact-label">이메일</label>
                <input
                  v-model="editFormData.emp_email"
                  type="email"
                  class="compact-input"
                  maxlength="50"
                />
              </div>
            </div>

            <div class="form-section">
              <h4 class="section-title">주소 정보</h4>
              
              <div class="compact-field">
                <label class="compact-label">주소</label>
                <div class="address-input-group">
                  <input
                    v-model="editFormData.emp_addr"
                    type="text"
                    class="compact-input"
                    placeholder="주소 검색 버튼을 클릭하세요"
                    readonly
                    @click="openEditAddressModal"
                  />
                  <button
                    type="button"
                    class="address-search-btn"
                    @click="openEditAddressModal"
                  >
                    주소검색
                  </button>
                </div>
              </div>
              
              <div class="compact-field">
                <label class="compact-label">상세주소</label>
                <input
                  v-model="editFormData.emp_addr_detail"
                  type="text"
                  class="compact-input"
                  placeholder="상세주소를 입력하세요"
                  maxlength="50"
                />
              </div>
            </div>

            <div class="form-section">
              <h4 class="section-title">재직 정보</h4>
              
              <div class="compact-field">
                <label class="compact-label">입사일</label>
                <input
                  v-model="editFormData.emp_in_date"
                  type="date"
                  class="compact-input"
                />
              </div>
              
              <div class="compact-field">
                <label class="compact-label">퇴사일</label>
                <input
                  v-model="editFormData.emp_out_date"
                  type="date"
                  class="compact-input"
                />
              </div>
            </div>
          </div>
        </v-card-text>
        
        <v-card-actions class="pa-4">
          <v-btn
            v-if="!isEditMode"
            variant="text"
            color="error"
            @click="confirmDeleteEmployee(selectedEmployee)"
          >
            <v-icon class="mr-1">mdi-delete</v-icon>
            삭제
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!isEditMode"
            variant="text"
            @click="detailDialog = false"
          >
            닫기
          </v-btn>
          <v-btn
            v-if="!isEditMode"
            color="#1e3a8a"
            variant="flat"
            @click="startEdit"
          >
            <v-icon class="mr-1">mdi-pencil</v-icon>
            수정하기
          </v-btn>
          
          <v-btn
            v-if="isEditMode"
            variant="text"
            @click="cancelEdit"
            :disabled="isSaving"
          >
            취소
          </v-btn>
          <v-btn
            v-if="isEditMode"
            color="#1e3a8a"
            variant="flat"
            @click="saveEdit"
            :loading="isSaving"
            :disabled="!isEditFormValid"
          >
            <v-icon class="mr-1">mdi-content-save</v-icon>
            저장
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 사원 등록/수정 모달 -->
    <EmployeeFormModal 
      v-model="employeeDialog"
      :employee="editingEmployee"
      @saved="handleEmployeeSaved"
    />

    <!-- 사원 정보 업로드 모달 -->
    <MemberUploadModal 
      v-model="memberUploadDialog"
      @uploaded="handleMemberUploaded"
    />

    <!-- 일정계획 캘린더 모달 -->
    <ScheduleCalendarModal
      v-model="scheduleCalendarDialog"
      :employee="selectedEmployeeForSchedule"
      :employees="filteredEmployees"
      @change-employee="handleScheduleEmployeeChange"
    />

    <!-- 사원 삭제 확인 모달 -->
    <v-dialog v-model="deleteDialog" max-width="500px">
      <v-card>
        <v-card-title class="delete-confirm-header">
          <v-icon color="white" class="mr-2">mdi-alert</v-icon>
          사원 삭제 확인
        </v-card-title>
        
        <v-card-text class="pa-6">
          <div class="delete-warning">
            <v-icon color="error" size="48" class="mb-3">mdi-alert-circle</v-icon>
            <p class="text-h6 mb-2">정말 삭제하시겠습니까?</p>
            <p class="text-body-2 text-grey-darken-1">
              <strong>{{ employeeToDelete?.emp_name }}</strong> 사원을 삭제합니다.<br>
              삭제된 사원은 목록에서 제외됩니다.
            </p>
          </div>
        </v-card-text>
        
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="deleteDialog = false"
            :disabled="isDeleting"
          >
            취소
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            @click="deleteEmployee"
            :loading="isDeleting"
          >
            삭제
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 사원 계정 부여 모달 -->
    <v-dialog v-model="credentialDialog" max-width="450px" >
      <v-card>
        <v-card-title class="credential-header">
          <v-icon color="white" class="mr-2">mdi-account-key</v-icon>
          {{ credentialState.hasAccount ? '사원 계정 수정' : '사원 계정 부여' }}
        </v-card-title>
        
        <v-card-text class="credential-content">
          <div class="employee-info-box">
            <v-avatar size="40" color="#1e3a8a" class="mr-3">
              <span class="avatar-text">{{ selectedEmployee?.emp_name?.charAt(0) }}</span>
            </v-avatar>
            <div>
              <div class="employee-name">{{ selectedEmployee?.emp_name }}</div>
              <div class="employee-job">{{ selectedEmployee?.emp_job }}</div>
            </div>
          </div>

          <div class="credential-form">
            <div class="compact-field">
              <label class="compact-label">아이디 *</label>
              <div class="id-input-group">
                <input
                  v-model="credentialForm.emp_id"
                  type="text"
                  class="compact-input"
                  placeholder="4~20자의 영문, 숫자"
                  maxlength="20"
                  @input="onCredentialIdInput"
                  :disabled="isCheckingId || isAssigningCredential"
                />
                <button
                  type="button"
                  class="id-check-btn"
                  :disabled="!credentialForm.emp_id || credentialForm.emp_id.length < 4 || isCheckingId"
                  @click="checkIdDuplicate"
                >
                  {{ isCheckingId ? '확인 중...' : '중복확인' }}
                </button>
              </div>
              <div
                v-if="idCheckResult.checked"
                class="field-message"
                :class="{ success: idCheckResult.available, error: !idCheckResult.available }"
              >
                {{ idCheckResult.message }}
              </div>
            </div>

            <div class="compact-field">
              <label class="compact-label">비밀번호 *</label>
              <div class="password-input-wrapper">
                <input
                  v-model="credentialForm.emp_pw"
                  :type="showPassword ? 'text' : 'password'"
                  class="compact-input"
                  placeholder="4자 이상"
                  maxlength="30"
                  :disabled="isAssigningCredential"
                />
                <button
                  type="button"
                  class="password-toggle-btn"
                  @click="showPassword = !showPassword"
                >
                  <v-icon size="18">{{ showPassword ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                </button>
              </div>
            </div>

            <div class="compact-field">
              <label class="compact-label">비밀번호 확인 *</label>
              <div class="password-input-wrapper">
                <input
                  v-model="credentialForm.emp_pw_confirm"
                  :type="showPasswordConfirm ? 'text' : 'password'"
                  class="compact-input"
                  placeholder="비밀번호 다시 입력"
                  maxlength="30"
                  :disabled="isAssigningCredential"
                />
                <button
                  type="button"
                  class="password-toggle-btn"
                  @click="showPasswordConfirm = !showPasswordConfirm"
                >
                  <v-icon size="18">{{ showPasswordConfirm ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                </button>
              </div>
              <div
                v-if="credentialForm.emp_pw && credentialForm.emp_pw_confirm"
                class="field-message"
                :class="{ success: isPasswordMatch, error: !isPasswordMatch }"
              >
                {{ isPasswordMatch ? '비밀번호가 일치합니다.' : '비밀번호가 일치하지 않습니다.' }}
              </div>
            </div>
          </div>
        </v-card-text>
        
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="closeCredentialDialog"
            :disabled="isAssigningCredential"
          >
            취소
          </v-btn>
          <v-btn
            color="#1e3a8a"
            variant="flat"
            @click="assignCredentials"
            :loading="isAssigningCredential"
            :disabled="!isCredentialFormValid"
          >
            <v-icon class="mr-1">mdi-check</v-icon>
            {{ credentialState.hasAccount ? '수정' : '부여' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 사원 문서 보기 모달 -->
    <v-dialog v-model="documentDialog" max-width="600px">
      <v-card>
        <v-card-title class="document-modal-header">
          <v-icon color="white" class="mr-2">mdi-file-document-multiple</v-icon>
          {{ selectedEmployeeForDoc?.emp_name }} 문서함
        </v-card-title>
        
        <v-card-text class="document-modal-content">
          <div v-if="isLoadingDocuments" class="loading-container">
            <v-progress-circular indeterminate color="#1e3a8a" />
            <span>문서를 불러오는 중...</span>
          </div>
          
          <div v-else-if="employeeDocuments.length === 0" class="empty-documents">
            <v-icon size="48" color="#9ca3af">mdi-file-document-outline</v-icon>
            <p>제출된 문서가 없습니다</p>
          </div>
          
          <div v-else class="document-list">
            <div 
              v-for="doc in employeeDocuments" 
              :key="doc.id"
              class="document-item"
              @click="viewDocumentDetail(doc)"
            >
              <div class="doc-icon" :class="doc.type">
                <v-icon size="24" color="white">
                  {{ doc.type === 'resignation' ? 'mdi-file-remove' : 'mdi-file-document' }}
                </v-icon>
              </div>
              <div class="doc-info">
                <span class="doc-title">
                  {{ doc.type === 'resignation' ? '사직서' : doc.type }}
                </span>
                <span class="doc-date">{{ formatDocDate(doc.date) }}</span>
              </div>
              <div class="doc-status" :class="doc.status">
                {{ getDocStatusText(doc.status) }}
              </div>
              <v-icon size="20" color="#9ca3af">mdi-chevron-right</v-icon>
            </div>
          </div>
        </v-card-text>
        
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="documentDialog = false">닫기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 문서 상세 보기 모달 (사직서) -->
    <v-dialog v-model="documentDetailDialog" max-width="500px">
      <v-card v-if="selectedDocument" class="resignation-detail-card">
        <div class="resignation-detail-header">
          <h2>사 직 서</h2>
          <button class="close-btn" @click="documentDetailDialog = false">
            <v-icon color="white">mdi-close</v-icon>
          </button>
        </div>
        
        <div class="resignation-detail-content">
          <div class="detail-form">
            <div class="form-row">
              <span class="form-label">소 속 :</span>
              <span class="form-value">{{ selectedDocument.content?.sisul_name || '-' }}</span>
            </div>
            <div class="form-row">
              <span class="form-label">직 위 :</span>
              <span class="form-value">{{ selectedDocument.content?.emp_job || selectedDocument.emp_job || '-' }}</span>
            </div>
            <div class="form-row">
              <span class="form-label">성 명 :</span>
              <span class="form-value">{{ selectedDocument.content?.emp_name || selectedDocument.emp_name || '-' }}</span>
            </div>
            <div class="form-row">
              <span class="form-label">사직사유 :</span>
              <span class="form-value reason">{{ selectedDocument.content?.resign_reason || '-' }}</span>
            </div>
          </div>

          <div class="detail-body">
            <p>상기 본인은 위와 같은 사정으로 인하여</p>
            <p class="resign-date">
              <strong>{{ formatResignDate(selectedDocument.resign_date) }}</strong> 부로
            </p>
            <p>사직하고자 이에 사직서를 제출합니다.</p>
          </div>

          <div class="submit-date">
            {{ formatDocDate(selectedDocument.submit_date) }}
          </div>

          <div class="signature-section">
            <span class="signature-label">신 청 인 :</span>
            <div class="signature-image" v-if="selectedDocument.signature_path">
              <img :src="getSignatureUrl(selectedDocument.signature_path)" alt="서명" />
            </div>
            <span class="seal-text">(인)</span>
          </div>

          <div class="status-section">
            <span class="status-badge" :class="selectedDocument.status">
              {{ getDocStatusText(selectedDocument.status) }}
            </span>
          </div>

          <!-- PDF 다운로드 버튼 -->
          <button 
            class="download-pdf-btn" 
            :class="{ disabled: !selectedDocument.pdf_path }"
            @click="downloadResignationPDF(selectedDocument.pdf_path)"
            :disabled="!selectedDocument.pdf_path"
          >
            <v-icon size="20">mdi-file-pdf-box</v-icon>
            <span>{{ selectedDocument.pdf_path ? '사직서 PDF 다운로드' : 'PDF 파일 없음' }}</span>
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- 일괄 계정 부여 모달 -->
    <v-dialog v-model="bulkCredentialDialog" max-width="800px" >
      <v-card>
        <v-card-title class="bulk-credential-header">
          <v-icon color="white" class="mr-2">mdi-account-multiple-plus</v-icon>
          일괄 계정 부여
        </v-card-title>
        
        <v-card-text class="bulk-credential-content">
          <!-- 설정 섹션 -->
          <div class="bulk-settings-section">
            <h4 class="section-title">ID 생성 규칙 설정</h4>
            <div class="settings-row">
              <div class="compact-field">
                <label class="compact-label">접두사 *</label>
                <input
                  v-model="bulkCredentialForm.prefix"
                  type="text"
                  class="compact-input"
                  placeholder="예: 행복요양"
                  maxlength="20"
                  :disabled="isBulkAssigning"
                />
                <div class="field-hint">
                  생성 예시: {{ bulkIdPreview }}
                </div>
              </div>
              <div class="compact-field">
                <label class="compact-label">공통 비밀번호 *</label>
                <input
                  v-model="bulkCredentialForm.password"
                  type="text"
                  class="compact-input"
                  placeholder="예: 1234"
                  maxlength="20"
                  :disabled="isBulkAssigning"
                />
              </div>
            </div>
            <div class="rule-info-box">
              <v-icon size="18" color="#1e3a8a" class="mr-2">mdi-information</v-icon>
              <div>
                <strong>ID 생성 규칙:</strong> {{ bulkCredentialForm.prefix || '접두사' }} + 이름 + 생년월일(6자리)<br>
                <strong>안내 문구 예시:</strong> "ID는 '{{ bulkCredentialForm.prefix || '접두사' }}'을 먼저 입력하고 본인이름과 생년월일(6자리)을 입력하시면 됩니다. 비밀번호는 {{ bulkCredentialForm.password || '1234' }}입니다."
              </div>
            </div>
            
            <!-- 중복검사 버튼 -->
            <div class="duplicate-check-section">
              <v-btn
                color="#1e3a8a"
                variant="outlined"
                :loading="isCheckingDuplicate"
                :disabled="!bulkCredentialForm.prefix || bulkSelectedEmployees.length === 0"
                @click="checkBulkDuplicate"
              >
                <v-icon class="mr-1">mdi-magnify-scan</v-icon>
                중복검사
              </v-btn>
              <div v-if="duplicateCheckResult.checked" class="duplicate-result" :class="duplicateCheckResult.hasError ? 'has-error' : 'no-error'">
                <v-icon size="18" :color="duplicateCheckResult.hasError ? '#dc2626' : '#16a34a'" class="mr-1">
                  {{ duplicateCheckResult.hasError ? 'mdi-alert-circle' : 'mdi-check-circle' }}
                </v-icon>
                {{ duplicateCheckResult.message }}
              </div>
            </div>
          </div>

          <!-- 대상 사원 목록 -->
          <div class="bulk-employee-section">
            <div class="section-header">
              <h4 class="section-title">대상 사원 목록</h4>
              <div class="bulk-actions">
                <v-btn
                  size="small"
                  variant="text"
                  color="#1e3a8a"
                  @click="selectAllEmployees"
                  :disabled="isBulkAssigning"
                >
                  전체 선택
                </v-btn>
                <v-btn
                  size="small"
                  variant="text"
                  @click="deselectAllEmployees"
                  :disabled="isBulkAssigning"
                >
                  전체 해제
                </v-btn>
              </div>
            </div>
            <div class="bulk-employee-list">
              <div class="bulk-employee-table-wrapper">
                <table class="bulk-employee-table">
                  <thead>
                    <tr>
                      <th class="checkbox-col">
                        <input
                          type="checkbox"
                          :checked="isAllSelected"
                          :indeterminate="isPartiallySelected"
                          @change="toggleSelectAll"
                          :disabled="isBulkAssigning"
                        />
                      </th>
                      <th>이름</th>
                      <th>직종</th>
                      <th>생년월일</th>
                      <th>현재 ID</th>
                      <th>생성될 ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr 
                      v-for="emp in employeesWithoutAccount" 
                      :key="emp.emp_idx"
                      :class="{ 
                        selected: bulkSelectedEmployees.includes(emp.emp_idx!),
                        'has-error': duplicateEmployees.includes(emp.emp_idx!)
                      }"
                    >
                      <td class="checkbox-col">
                        <input
                          type="checkbox"
                          :checked="bulkSelectedEmployees.includes(emp.emp_idx!)"
                          @change="toggleEmployeeSelection(emp.emp_idx!)"
                          :disabled="isBulkAssigning"
                        />
                      </td>
                      <td>
                        {{ emp.emp_name }}
                        <v-icon v-if="duplicateEmployees.includes(emp.emp_idx!)" size="16" color="#dc2626" class="ml-1">mdi-alert</v-icon>
                      </td>
                      <td>{{ emp.emp_job }}</td>
                      <td>{{ formatBirth(emp.emp_birth) }}</td>
                      <td>
                        <span class="no-id-badge">
                          <v-icon size="14" color="#9ca3af" class="mr-1">mdi-close-circle-outline</v-icon>
                          미부여
                        </span>
                      </td>
                      <td>
                        <span class="generated-id" :class="{ 'error-id': duplicateEmployees.includes(emp.emp_idx!) }">
                          {{ generateBulkId(emp) }}
                          <span v-if="getDuplicateReason(emp.emp_idx!)" class="error-reason">
                            ({{ getDuplicateReason(emp.emp_idx!) }})
                          </span>
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="bulk-summary">
              <span>계정 미부여 사원 <strong>{{ employeesWithoutAccount.length }}명</strong> 중 <strong>{{ bulkSelectedEmployees.length }}명</strong> 선택됨</span>
            </div>
          </div>
        </v-card-text>
        
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="closeBulkCredentialDialog"
            :disabled="isBulkAssigning"
          >
            취소
          </v-btn>
          <v-btn
            color="#1e3a8a"
            variant="flat"
            @click="assignBulkCredentials"
            :loading="isBulkAssigning"
            :disabled="!isBulkFormValid"
          >
            <v-icon class="mr-1">mdi-account-multiple-check</v-icon>
            {{ bulkSelectedEmployees.length }}명 계정 부여
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 일정계획 업로드 모달 -->
    <v-dialog v-model="scheduleUploadDialog" max-width="600px" :persistent="isScheduleUploading">
      <v-card>
        <v-card-title class="schedule-upload-header">
          <v-icon color="white" class="mr-2">mdi-calendar</v-icon>
          일정계획 파일 업로드
        </v-card-title>
        
        <v-card-text class="schedule-upload-content">
          <div class="download-guide">
            <h4>파일 다운로드 안내</h4>
            <ol>
              <li>장기요양정보시스템 접속 후 우측 상단 <strong>기타</strong> &gt; <strong>엑셀 다운로드</strong>를 클릭합니다.</li>
              <li><strong>일정계획</strong> 탭에서 <strong>엑셀 다운로드</strong> 버튼을 눌러 파일을 저장합니다.</li>
              <li>브라우저 저장 창에서 <strong>Ctrl + Shift + S</strong>로 파일을 저장해 주세요.</li>
            </ol>
          </div>

          <!-- 업로드 현황 -->
          <div class="upload-status-section">
            <div class="upload-status-header">
              <span class="status-year">{{ currentYear }}년 업로드 현황</span>
              <v-btn 
                icon 
                size="x-small" 
                variant="text" 
                @click="fetchScheduleUploadStatus"
                :loading="isLoadingScheduleStatus"
              >
                <v-icon size="16">mdi-refresh</v-icon>
              </v-btn>
            </div>
            <div class="month-status-grid">
              <div 
                v-for="m in 12" 
                :key="m" 
                class="month-status-item"
                :class="{ 'has-data': scheduleUploadStatus[m] }"
              >
                <span class="month-num">{{ m }}월</span>
                <v-icon 
                  size="14" 
                  :color="scheduleUploadStatus[m] ? '#059669' : '#d1d5db'"
                >
                  {{ scheduleUploadStatus[m] ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                </v-icon>
              </div>
            </div>
          </div>

          <div 
            class="schedule-dropzone"
            :class="{ 'is-active': isScheduleDragging, 'is-disabled': isScheduleUploading }"
            @drop.prevent="handleScheduleDrop"
            @dragover.prevent="handleScheduleDragOver"
            @dragleave.prevent="handleScheduleDragLeave"
            @click="triggerScheduleFileInput"
          >
            <v-icon size="48" :color="isScheduleDragging ? '#059669' : '#94a3b8'">mdi-file-excel</v-icon>
            <h4>일정계획 파일을 드래그하거나 클릭하세요</h4>
            <p>엑셀 파일 (xls, xlsx)</p>
          </div>
          
          <input
            ref="scheduleFileInput"
            type="file"
            accept=".xls,.xlsx"
            style="display: none"
            @change="handleScheduleFileSelect"
          />
          
          <div v-if="scheduleFile" class="selected-file-card">
            <div class="file-icon">
              <v-icon color="#059669" size="32">mdi-file-excel</v-icon>
            </div>
            <div class="file-info">
              <span class="file-name">{{ scheduleFile.name }}</span>
              <span class="file-size">{{ formatFileSize(scheduleFile.size) }}</span>
            </div>
            <v-btn icon size="small" variant="text" @click="clearScheduleFile" :disabled="isScheduleUploading">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <div class="upload-info-box">
            <v-icon size="18" color="#059669" class="mr-2">mdi-information</v-icon>
            <div>
              <strong>안내:</strong> 장기요양정보시스템에서 다운로드한 일정계획 엑셀 파일을 업로드하세요.
            </div>
          </div>
        </v-card-text>
        
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="closeScheduleUploadDialog"
            :disabled="isScheduleUploading"
          >
            취소
          </v-btn>
          <v-btn
            color="#059669"
            variant="flat"
            @click="uploadScheduleFile"
            :loading="isScheduleUploading"
            :disabled="!scheduleFile"
          >
            <v-icon class="mr-1">mdi-upload</v-icon>
            업로드
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, nextTick, watch } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import EmployeeFormModal from '@/components/employee/EmployeeFormModal.vue';
import MemberUploadModal from '@/components/employee/MemberUploadModal.vue';
import ScheduleCalendarModal from '@/components/employee/ScheduleCalendarModal.vue';

const store = useStore();
const user = computed(() => store.state.user);

interface Employee {
  emp_idx?: number;
  emp_code: string;
  emp_id?: string;
  m_sisul_code: string;
  emp_name: string;
  emp_birth: string;
  emp_birth_number?: string;
  emp_job: string;
  emp_in_date?: string;
  emp_out_date?: string;
  emp_phone?: string;
  emp_email?: string;
  emp_addr?: string;
  emp_addr_detail?: string;
  emp_wdate?: string;
  emp_rdate?: string;
  emp_status?: 'ACTIVE' | 'INACTIVE' | 'RETIRING';
  has_birth_number?: boolean | number | string;
  doc_count?: number;
  has_resignation?: number;
  resignation_status?: string;
}

// 상태 관리
const isLoading = ref(false);
const loadingText = ref('로딩 중...');
const detailDialog = ref(false);
const employeeDialog = ref(false);
const deleteDialog = ref(false);
const selectedEmployee = ref<Employee | null>(null);
const editingEmployee = ref<Employee | null>(null);
const employeeToDelete = ref<Employee | null>(null);
const isSaving = ref(false);
const isDeleting = ref(false);
const isEditMode = ref(false);

// 사원 목록
const employees = ref<Employee[]>([]);

// 필터
const filters = ref({
  keyword: '',
  job: null as string | null,
  status: null as 'ACTIVE' | 'INACTIVE' | 'RETIRING' | null
});

// 사원 정보 업로드 관련
const memberUploadDialog = ref(false);

// 일정계획 캘린더 관련
const scheduleCalendarDialog = ref(false);
const selectedEmployeeForSchedule = ref<Employee | null>(null);

// 일정계획 업로드 관련
const scheduleUploadDialog = ref(false);
const scheduleFile = ref<File | null>(null);
const scheduleFileInput = ref<HTMLInputElement | null>(null);
const isScheduleUploading = ref(false);
const isScheduleDragging = ref(false);
const currentYear = new Date().getFullYear();
const scheduleUploadStatus = ref<Record<number, boolean>>({});
const isLoadingScheduleStatus = ref(false);

// 수정 폼 데이터 (상세 모달용)
const editFormData = ref<Employee>({
  emp_code: '',
  m_sisul_code: '',
  emp_name: '',
  emp_birth: '',
  emp_birth_number: '',
  emp_job: '',
  emp_in_date: '',
  emp_out_date: '',
  emp_phone: '',
  emp_email: '',
  emp_addr: '',
  emp_addr_detail: ''
});

// 주민등록번호 입력 상태 (UI용)
const birthNumberFront = ref('');
const birthNumberBack = ref('');

// 주소 검색 관련 (상세 모달 수정 모드용)
const addressDialog = ref(false);
const addressLoading = ref(false);
const postcodeContainer = ref<HTMLDivElement | null>(null);
let daumScriptLoading: Promise<void> | null = null;

// 주민등록번호 복호화 상태
const birthNumberState = ref({
  visible: false,
  loading: false,
  value: ''
});

// 사원 계정 부여 관련
const credentialDialog = ref(false);
const isCheckingId = ref(false);
const isAssigningCredential = ref(false);
const showPassword = ref(false);
const showPasswordConfirm = ref(false);

const credentialState = ref({
  hasAccount: false,
  empId: ''
});

const credentialForm = ref({
  emp_id: '',
  emp_pw: '',
  emp_pw_confirm: ''
});

const idCheckResult = ref({
  checked: false,
  available: false,
  message: ''
});

// 일괄 계정 부여 관련
const bulkCredentialDialog = ref(false);
const isBulkAssigning = ref(false);
const bulkSelectedEmployees = ref<number[]>([]);
const bulkCredentialForm = ref({
  prefix: '',
  password: '1234'
});

// 중복 검사 관련
const isCheckingDuplicate = ref(false);
const duplicateEmployees = ref<number[]>([]);
const duplicateReasons = ref<Record<number, string>>({});
const duplicateCheckResult = ref({
  checked: false,
  hasError: false,
  message: ''
});

// 옵션
const jobOptions = ['센터장', '사회복지사', '요양보호사', '간호사', '간호조무사', '물리치료사', '작업치료사', '기타'];

// 다운로드 가능한 파일 목록
const downloadableFiles = [
  {
    name: '취업규칙(요양시설)-2025년(협력 노무사 감수).hwp',
    label: '취업규칙 (요양시설) 2025년',
    path: '/files/취업규칙(요양시설)-2025년(협력 노무사 감수).hwp'
  },
  {
    name: '방문요양 요양보호사 근로계약서 샘플 2026년.hwpx',
    label: '근로계약서 샘플 2026년',
    path: '/files/방문요양 요양보호사 근로계약서 샘플 2026년.hwpx'
  }
];

// 파일 다운로드 함수
const downloadFile = (file: { name: string; label: string; path: string }) => {
  const link = document.createElement('a');
  link.href = file.path;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  store.commit('showSnackbar', {
    text: `${file.label} 파일을 다운로드합니다.`,
    color: 'success'
  });
};

const statusOptions = [
  { label: '재직', value: 'ACTIVE' },
  { label: '퇴사/휴직', value: 'INACTIVE' },
  { label: '퇴사 예정', value: 'RETIRING' }
];

// 테이블 헤더
const headers = [
  { title: '이름', value: 'emp_name', sortable: true },
  { title: '직종', value: 'emp_job', sortable: true },
  { title: 'ID', value: 'emp_id', sortable: true },
  { title: '생년월일', value: 'emp_birth', sortable: true },
  { title: '휴대폰번호', value: 'emp_phone', sortable: false },
  { title: '입사일', value: 'emp_in_date', sortable: true },
  { title: '퇴사일', value: 'emp_out_date', sortable: true },
  { title: '문서', value: 'documents', sortable: false, align: 'center' as const },
  { title: '작업', value: 'actions', sortable: false, align: 'center' as const }
];

// 문서 모달 관련
const documentDialog = ref(false);
const selectedEmployeeForDoc = ref<Employee | null>(null);
const employeeDocuments = ref<any[]>([]);
const isLoadingDocuments = ref(false);
const selectedDocument = ref<any>(null);
const documentDetailDialog = ref(false);

// 계산된 속성
const filteredEmployees = computed(() => {
  return employees.value.filter(employee => {
    const keywordMatch =
      !filters.value.keyword ||
      [employee.emp_name, employee.emp_code, employee.emp_phone, employee.emp_email]
        .filter(Boolean)
        .some(field => field?.toLowerCase().includes(filters.value.keyword.toLowerCase()));

    const jobMatch = !filters.value.job || employee.emp_job === filters.value.job;
    const statusMatch = !filters.value.status || employee.emp_status === filters.value.status;

    return keywordMatch && jobMatch && statusMatch;
  });
});

const employeesSummary = computed(() => ({
  total: employees.value.length,
  active: employees.value.filter(emp => emp.emp_status === 'ACTIVE').length,
  inactive: employees.value.filter(emp => emp.emp_status !== 'ACTIVE').length
}));

const isEditFormValid = computed(() => {
  return !!(
    editFormData.value.emp_name &&
    editFormData.value.emp_birth &&
    editFormData.value.emp_job
  );
});

const hasBirthNumberRegistered = computed(() => {
  const value = selectedEmployee.value?.has_birth_number;
  if (value === undefined || value === null) {
    return false;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value > 0;
  }

  if (typeof value === 'string') {
    return value === '1' || value.toLowerCase() === 'true';
  }

  return false;
});

// 생년월일에서 주민등록번호 앞자리 추출 (YYYYMMDD → YYMMDD)
const extractBirthFront = (birth: string): string => {
  if (!birth) return '';
  // YYYYMMDD 형식에서 YYMMDD 추출
  const digitsOnly = birth.replace(/[^\d]/g, '');
  if (digitsOnly.length === 8) {
    return digitsOnly.substring(2, 8);
  }
  return '';
};

// 주민등록번호 포맷팅 (있는 그대로 표시)
const formatBirthNumber = (birthNumber: string, empBirth?: string): string => {
  if (!birthNumber) return '';
  const digitsOnly = birthNumber.replace(/[^\d]/g, '');
  
  // 정상적인 13자리인 경우
  if (digitsOnly.length === 13) {
    return `${digitsOnly.substring(0, 6)}-${digitsOnly.substring(6)}`;
  }
  
  // 7자리(뒷자리만)인 경우: 앞자리 없음 표시
  if (digitsOnly.length === 7) {
    return `(앞자리 없음) ${digitsOnly}`;
  }
  
  // 그 외의 경우 있는 그대로 표시
  if (digitsOnly.length > 0) {
    return digitsOnly;
  }
  
  return '●●●●●●-●●●●●●●';
};

// 주민등록번호 마스킹 텍스트 (항상 마스킹으로 표시)
const getBirthNumberMaskedText = (): string => {
  return '●●●●●●-●●●●●●●';
};

// 주민등록번호 상태 텍스트 (수정 모드용)
const getBirthNumberStatusText = (): string => {
  // has_birth_number 여부로 판단
  if (hasBirthNumberRegistered.value) {
    return '등록됨';
  }
  return '미등록';
};

const isPasswordMatch = computed(() => {
  return credentialForm.value.emp_pw === credentialForm.value.emp_pw_confirm;
});

const isCredentialFormValid = computed(() => {
  return (
    credentialForm.value.emp_id.length >= 4 &&
    credentialForm.value.emp_pw.length >= 4 &&
    isPasswordMatch.value &&
    (idCheckResult.value.checked && idCheckResult.value.available)
  );
});

// 일괄 계정 부여 관련 계산된 속성
// 계정이 없는 재직 중인 사원만 표시
const employeesWithoutAccount = computed(() => {
  return employees.value.filter(emp => 
    emp.emp_status === 'ACTIVE' && 
    (!emp.emp_id || emp.emp_id.trim() === '')
  );
});

const isAllSelected = computed(() => {
  return employeesWithoutAccount.value.length > 0 && 
    bulkSelectedEmployees.value.length === employeesWithoutAccount.value.length;
});

const isPartiallySelected = computed(() => {
  return bulkSelectedEmployees.value.length > 0 && 
    bulkSelectedEmployees.value.length < employeesWithoutAccount.value.length;
});

const bulkIdPreview = computed(() => {
  const prefix = bulkCredentialForm.value.prefix || '접두사';
  return `${prefix}홍길동990101`;
});

const isBulkFormValid = computed(() => {
  return (
    bulkCredentialForm.value.prefix.trim().length > 0 &&
    bulkCredentialForm.value.password.trim().length >= 4 &&
    bulkSelectedEmployees.value.length > 0 &&
    duplicateCheckResult.value.checked &&
    !duplicateCheckResult.value.hasError
  );
});

// 메서드
const fetchEmployees = async () => {
  if (!user.value?.m_sisul_code) {
    console.error('시설 코드가 없습니다.', user.value);
    store.commit('showSnackbar', {
      text: '사용자 정보를 불러올 수 없습니다. 다시 로그인해주세요.',
      color: 'error'
    });
    return;
  }

  try {
    isLoading.value = true;
    loadingText.value = '사원 정보를 불러오는 중...';
    
    const response = await axios.get(`/api/emp/facility/${user.value.m_sisul_code}`, {
      withCredentials: true
    });


    if (response.data.success) {
      employees.value = response.data.data || [];
      
      if (employees.value.length === 0) {
        store.commit('showSnackbar', {
          text: '등록된 사원이 없습니다.',
          color: 'info'
        });
      }
    } else {
      console.error('사원 조회 실패:', response.data.message);
      store.commit('showSnackbar', {
        text: response.data.message || '사원 정보 조회에 실패했습니다.',
        color: 'error'
      });
    }
  } catch (error: any) {
    console.error('사원 조회 중 오류:', error);
    console.error('에러 상세:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      url: error.config?.url
    });
    
    const errorMessage = error.response?.data?.message 
      || error.response?.data?.error
      || error.message
      || '사원 정보 조회 중 오류가 발생했습니다.';
    
    store.commit('showSnackbar', {
      text: errorMessage,
      color: 'error'
    });
  } finally {
    isLoading.value = false;
  }
};

const resetFilters = () => {
  filters.value = {
    keyword: '',
    job: null,
    status: null
  };
};

const getStatusColor = (status?: string) => {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'RETIRING':
      return 'warning';
    case 'INACTIVE':
      return 'error';
    default:
      return 'grey';
  }
};

const getStatusText = (status?: string) => {
  switch (status) {
    case 'ACTIVE':
      return '재직';
    case 'RETIRING':
      return '퇴사예정';
    case 'INACTIVE':
      return '퇴사/휴직';
    default:
      return '-';
  }
};

const formatBirth = (birth: string) => {
  if (!birth || birth.length !== 8) return birth;
  return `${birth.substring(0, 4)}-${birth.substring(4, 6)}-${birth.substring(6, 8)}`;
};

// 문서 날짜 포맷
const formatDocDate = (dateStr: string) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
};

// 퇴사 예정일 포맷
const formatResignDate = (dateStr: string) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
};

// 문서 상태 텍스트
const getDocStatusText = (status: string) => {
  switch (status) {
    case 'submitted': return '제출됨';
    case 'approved': return '승인됨';
    case 'rejected': return '반려됨';
    default: return status;
  }
};

// 사직서 배지 색상
const getResignationBadgeColor = (status: string) => {
  switch (status) {
    case 'submitted': return '#f59e0b'; // 주황색 (대기중)
    case 'approved': return '#dc2626'; // 빨간색 (승인됨)
    case 'rejected': return '#6b7280'; // 회색 (반려됨)
    default: return '#f59e0b';
  }
};

// 사직서 배지 텍스트
const getResignationBadgeText = (status: string) => {
  switch (status) {
    case 'submitted': return '사직서';
    case 'approved': return '사직승인';
    case 'rejected': return '사직반려';
    default: return '사직서';
  }
};

// 문서 모달 열기
const openDocumentDialog = async (employee: Employee) => {
  selectedEmployeeForDoc.value = employee;
  documentDialog.value = true;
  await fetchEmployeeDocuments(employee.emp_idx!);
};

// 사원 문서 조회
const fetchEmployeeDocuments = async (empIdx: number) => {
  isLoadingDocuments.value = true;
  try {
    const response = await axios.get(`/api/emp/documents/${empIdx}`, {
      withCredentials: true
    });
    
    if (response.data.success) {
      employeeDocuments.value = response.data.data || [];
    } else {
      employeeDocuments.value = [];
    }
  } catch (error) {
    console.error('문서 조회 오류:', error);
    employeeDocuments.value = [];
  } finally {
    isLoadingDocuments.value = false;
  }
};

// 문서 상세 보기
const viewDocumentDetail = (doc: any) => {
  selectedDocument.value = doc;
  documentDetailDialog.value = true;
};

// 서명 이미지 URL 생성
const getSignatureUrl = (signaturePath: string) => {
  if (!signaturePath) return '';
  // /emp_sign/파일명.png → /api/emp/emp_sign/파일명.png
  return `/api/emp${signaturePath}`;
};

// 사직서 PDF 다운로드
const downloadResignationPDF = (pdfPath: string) => {
  if (!pdfPath) return;
  // /emp_docs/파일명.pdf → /api/emp/emp_docs/파일명.pdf
  const pdfUrl = `/api/emp${pdfPath}`;
  window.open(pdfUrl, '_blank');
};

const viewEmployeeDetail = async (employee: Employee) => {
  selectedEmployee.value = employee;
  isEditMode.value = false;
  birthNumberState.value = {
    visible: false,
    loading: false,
    value: ''
  };
  birthNumberFront.value = '';
  birthNumberBack.value = '';
  // 계정 정보 초기화 및 조회
  credentialState.value = {
    hasAccount: false,
    empId: ''
  };
  if (employee.emp_idx) {
    await fetchCredentialInfo(employee.emp_idx);
  }
  detailDialog.value = true;
};

// 사원 계정 정보 조회
const fetchCredentialInfo = async (empIdx: number) => {
  try {
    const response = await axios.get(`/api/emp/facility/${empIdx}/credentials`, {
      withCredentials: true
    });

    if (response.data.success && response.data.data) {
      credentialState.value = {
        hasAccount: response.data.data.has_account,
        empId: response.data.data.emp_id || ''
      };
    }
  } catch (error) {
    console.error('계정 정보 조회 오류:', error);
  }
};

// 계정 부여 모달 열기
const openCredentialDialog = () => {
  // 폼 초기화
  credentialForm.value = {
    emp_id: credentialState.value.empId || '',
    emp_pw: '',
    emp_pw_confirm: ''
  };
  idCheckResult.value = {
    checked: credentialState.value.hasAccount && credentialState.value.empId ? true : false,
    available: credentialState.value.hasAccount && credentialState.value.empId ? true : false,
    message: credentialState.value.hasAccount ? '기존 아이디입니다.' : ''
  };
  showPassword.value = false;
  showPasswordConfirm.value = false;
  credentialDialog.value = true;
};

// 계정 부여 모달 닫기
const closeCredentialDialog = () => {
  credentialDialog.value = false;
  credentialForm.value = {
    emp_id: '',
    emp_pw: '',
    emp_pw_confirm: ''
  };
  idCheckResult.value = {
    checked: false,
    available: false,
    message: ''
  };
};

// 아이디 입력 핸들러
const onCredentialIdInput = () => {
  // 아이디가 변경되면 중복 확인 결과 리셋
  // 단, 기존 아이디와 같으면 리셋하지 않음
  if (credentialState.value.hasAccount && credentialForm.value.emp_id === credentialState.value.empId) {
    idCheckResult.value = {
      checked: true,
      available: true,
      message: '기존 아이디입니다.'
    };
  } else {
    idCheckResult.value = {
      checked: false,
      available: false,
      message: ''
    };
  }
};

// 아이디 중복 확인
const checkIdDuplicate = async () => {
  if (!credentialForm.value.emp_id || credentialForm.value.emp_id.length < 4) {
    store.commit('showSnackbar', {
      text: '아이디는 4자 이상 입력해주세요.',
      color: 'warning'
    });
    return;
  }

  // 기존 아이디와 같으면 중복 확인 불필요
  if (credentialState.value.hasAccount && credentialForm.value.emp_id === credentialState.value.empId) {
    idCheckResult.value = {
      checked: true,
      available: true,
      message: '기존 아이디입니다.'
    };
    return;
  }

  try {
    isCheckingId.value = true;
    const response = await axios.get(`/api/emp/check-id/${credentialForm.value.emp_id}`, {
      withCredentials: true
    });

    idCheckResult.value = {
      checked: true,
      available: response.data.available,
      message: response.data.message
    };
  } catch (error: any) {
    console.error('아이디 중복 확인 오류:', error);
    idCheckResult.value = {
      checked: true,
      available: false,
      message: '중복 확인 중 오류가 발생했습니다.'
    };
  } finally {
    isCheckingId.value = false;
  }
};

// 계정 부여/수정 실행
const assignCredentials = async () => {
  if (!isCredentialFormValid.value) return;
  if (!selectedEmployee.value?.emp_idx) {
    store.commit('showSnackbar', {
      text: '사원 정보가 없습니다.',
      color: 'error'
    });
    return;
  }

  try {
    isAssigningCredential.value = true;
    
    const response = await axios.put(
      `/api/emp/facility/${selectedEmployee.value.emp_idx}/credentials`,
      {
        emp_id: credentialForm.value.emp_id,
        emp_pw: credentialForm.value.emp_pw
      },
      { withCredentials: true }
    );

    if (response.data.success) {
      store.commit('showSnackbar', {
        text: response.data.message || '계정이 성공적으로 부여되었습니다.',
        color: 'success'
      });
      
      // 계정 상태 업데이트
      credentialState.value = {
        hasAccount: true,
        empId: credentialForm.value.emp_id
      };
      
      closeCredentialDialog();
    } else {
      store.commit('showSnackbar', {
        text: response.data.message || '계정 부여에 실패했습니다.',
        color: 'error'
      });
    }
  } catch (error: any) {
    console.error('계정 부여 오류:', error);
    store.commit('showSnackbar', {
      text: error.response?.data?.message || '계정 부여 중 오류가 발생했습니다.',
      color: 'error'
    });
  } finally {
    isAssigningCredential.value = false;
  }
};

// 일괄 계정 부여 관련 함수들
const generateBulkId = (emp: Employee): string => {
  if (!bulkCredentialForm.value.prefix) return '-';
  const birthPart = emp.emp_birth ? emp.emp_birth.substring(2, 8) : '';
  return `${bulkCredentialForm.value.prefix}${emp.emp_name}${birthPart}`;
};

const openBulkCredentialDialog = () => {
  bulkCredentialForm.value = {
    prefix: '',
    password: '1234'
  };
  // 미부여 사원 전체 선택
  bulkSelectedEmployees.value = employeesWithoutAccount.value
    .filter(emp => emp.emp_idx !== undefined)
    .map(emp => emp.emp_idx!);
  // 중복 검사 결과 초기화
  duplicateCheckResult.value = {
    checked: false,
    hasError: false,
    message: ''
  };
  duplicateEmployees.value = [];
  duplicateReasons.value = {};
  bulkCredentialDialog.value = true;
};

const closeBulkCredentialDialog = () => {
  bulkCredentialDialog.value = false;
  bulkCredentialForm.value = {
    prefix: '',
    password: '1234'
  };
  bulkSelectedEmployees.value = [];
};

const selectAllEmployees = () => {
  bulkSelectedEmployees.value = employeesWithoutAccount.value
    .filter(emp => emp.emp_idx !== undefined)
    .map(emp => emp.emp_idx!);
};

const deselectAllEmployees = () => {
  bulkSelectedEmployees.value = [];
};

const toggleSelectAll = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.checked) {
    selectAllEmployees();
  } else {
    deselectAllEmployees();
  }
};

const toggleEmployeeSelection = (empIdx: number) => {
  const index = bulkSelectedEmployees.value.indexOf(empIdx);
  if (index === -1) {
    bulkSelectedEmployees.value.push(empIdx);
  } else {
    bulkSelectedEmployees.value.splice(index, 1);
  }
  // 선택 변경 시 중복 검사 결과 초기화
  resetDuplicateCheck();
};

// 중복 검사 결과 초기화
const resetDuplicateCheck = () => {
  duplicateCheckResult.value = {
    checked: false,
    hasError: false,
    message: ''
  };
  duplicateEmployees.value = [];
  duplicateReasons.value = {};
};

// 중복 사유 조회
const getDuplicateReason = (empIdx: number): string => {
  return duplicateReasons.value[empIdx] || '';
};

// 중복 검사 실행
const checkBulkDuplicate = async () => {
  if (!bulkCredentialForm.value.prefix || bulkSelectedEmployees.value.length === 0) {
    store.commit('showSnackbar', {
      text: '접두사를 입력하고 사원을 선택해주세요.',
      color: 'warning'
    });
    return;
  }

  try {
    isCheckingDuplicate.value = true;
    duplicateEmployees.value = [];
    duplicateReasons.value = {};
    
    // 1. 선택된 사원들의 생성될 ID 목록 만들기
    const idList: { empIdx: number; empId: string; empName: string }[] = [];
    for (const empIdx of bulkSelectedEmployees.value) {
      const emp = employees.value.find(e => e.emp_idx === empIdx);
      if (emp) {
        const birthPart = emp.emp_birth ? emp.emp_birth.substring(2, 8) : '';
        idList.push({
          empIdx: empIdx,
          empId: `${bulkCredentialForm.value.prefix}${emp.emp_name}${birthPart}`,
          empName: emp.emp_name
        });
      }
    }

    // 2. 프론트엔드에서 생성될 ID끼리 중복 체크
    const idCount: Record<string, number[]> = {};
    for (const item of idList) {
      if (!idCount[item.empId]) {
        idCount[item.empId] = [];
      }
      idCount[item.empId].push(item.empIdx);
    }

    // 같은 ID가 2개 이상이면 중복
    for (const [id, empIdxList] of Object.entries(idCount)) {
      if (empIdxList.length > 1) {
        for (const empIdx of empIdxList) {
          if (!duplicateEmployees.value.includes(empIdx)) {
            duplicateEmployees.value.push(empIdx);
          }
          duplicateReasons.value[empIdx] = '동일 ID 생성됨';
        }
      }
    }

    // 3. 백엔드에서 기존 ID와 중복 체크
    const response = await axios.post(
      '/api/emp/check-bulk-ids',
      { ids: idList.map(item => ({ empIdx: item.empIdx, empId: item.empId })) },
      { withCredentials: true }
    );

    if (response.data.success && response.data.duplicates) {
      for (const dup of response.data.duplicates) {
        if (!duplicateEmployees.value.includes(dup.empIdx)) {
          duplicateEmployees.value.push(dup.empIdx);
        }
        duplicateReasons.value[dup.empIdx] = dup.reason || '이미 사용 중인 ID';
      }
    }

    // 4. 결과 설정
    if (duplicateEmployees.value.length > 0) {
      duplicateCheckResult.value = {
        checked: true,
        hasError: true,
        message: `${duplicateEmployees.value.length}명의 ID가 중복되었습니다. 빨간색 표시된 사원을 확인해주세요.`
      };
    } else {
      duplicateCheckResult.value = {
        checked: true,
        hasError: false,
        message: '중복된 ID가 없습니다. 계정을 부여할 수 있습니다.'
      };
    }
  } catch (error: any) {
    console.error('중복 검사 오류:', error);
    duplicateCheckResult.value = {
      checked: true,
      hasError: true,
      message: '중복 검사 중 오류가 발생했습니다.'
    };
  } finally {
    isCheckingDuplicate.value = false;
  }
};

const assignBulkCredentials = async () => {
  if (!isBulkFormValid.value) return;

  try {
    isBulkAssigning.value = true;
    
    // 선택된 사원들의 계정 정보 생성
    const credentialsList = bulkSelectedEmployees.value.map(empIdx => {
      const emp = employees.value.find(e => e.emp_idx === empIdx);
      if (!emp) return null;
      
      const birthPart = emp.emp_birth ? emp.emp_birth.substring(2, 8) : '';
      return {
        emp_idx: empIdx,
        emp_id: `${bulkCredentialForm.value.prefix}${emp.emp_name}${birthPart}`,
        emp_pw: bulkCredentialForm.value.password
      };
    }).filter(item => item !== null);

    const response = await axios.post(
      '/api/emp/facility/bulk-credentials',
      { credentials: credentialsList },
      { withCredentials: true }
    );

    if (response.data.success) {
      store.commit('showSnackbar', {
        text: response.data.message || `${credentialsList.length}명의 계정이 성공적으로 부여되었습니다.`,
        color: 'success'
      });
      
      closeBulkCredentialDialog();
      await fetchEmployees();
    } else {
      store.commit('showSnackbar', {
        text: response.data.message || '일괄 계정 부여에 실패했습니다.',
        color: 'error'
      });
    }
  } catch (error: any) {
    console.error('일괄 계정 부여 오류:', error);
    store.commit('showSnackbar', {
      text: error.response?.data?.message || '일괄 계정 부여 중 오류가 발생했습니다.',
      color: 'error'
    });
  } finally {
    isBulkAssigning.value = false;
  }
};

const resetBirthNumberState = () => {
  birthNumberState.value = {
    visible: false,
    loading: false,
    value: ''
  };
  birthNumberBack.value = '';
};

const fetchBirthNumberFromServer = async (): Promise<string | null> => {
  if (!selectedEmployee.value?.emp_idx) {
    return null;
  }

  try {
    birthNumberState.value.loading = true;
    const response = await axios.get(`/api/emp/facility/${selectedEmployee.value.emp_idx}/birth-number`, {
      withCredentials: true
    });

    if (response.data.success && response.data.data?.birthNumber) {
      birthNumberState.value.value = response.data.data.birthNumber;
      return response.data.data.birthNumber;
    }

    store.commit('showSnackbar', {
      text: response.data.message || '등록된 주민등록번호가 없습니다.',
      color: 'info'
    });
    return null;
  } catch (error) {
    console.error('주민등록번호 복호화 오류:', error);
    store.commit('showSnackbar', {
      text: '주민등록번호를 불러오지 못했습니다.',
      color: 'error'
    });
    return null;
  } finally {
    birthNumberState.value.loading = false;
  }
};

const toggleBirthNumberVisibility = async () => {
  if (!hasBirthNumberRegistered.value) {
    return;
  }

  if (birthNumberState.value.visible) {
    birthNumberState.value.visible = false;
    return;
  }

  if (!birthNumberState.value.value) {
    const fetched = await fetchBirthNumberFromServer();
    if (!fetched) {
      return;
    }
  }

  birthNumberState.value.visible = true;
};

const fillBirthNumberForEdit = async () => {
  if (!hasBirthNumberRegistered.value) {
    return;
  }

  if (!birthNumberState.value.value) {
    const fetched = await fetchBirthNumberFromServer();
    if (!fetched) {
      return;
    }
  }

  // 주민등록번호에서 앞자리 6자리 + 뒷자리 7자리 추출
  const fullNumber = birthNumberState.value.value.replace(/[^\d]/g, '');
  if (fullNumber.length === 13) {
    // 전체 13자리가 있으면 앞자리와 뒷자리 분리
    birthNumberFront.value = fullNumber.slice(0, 6);
    birthNumberBack.value = fullNumber.slice(6);
  } else if (fullNumber.length === 7) {
    // 뒷자리만 있는 경우
    birthNumberFront.value = '';
    birthNumberBack.value = fullNumber;
  } else if (fullNumber.length >= 7) {
    birthNumberBack.value = fullNumber.slice(-7);
  }
  store.commit('showSnackbar', {
    text: '주민등록번호를 입력란에 불러왔습니다.',
    color: 'success'
  });
};

const startEdit = async () => {
  if (selectedEmployee.value) {
    editFormData.value = { ...selectedEmployee.value, emp_birth_number: '' };
    // 앞자리, 뒷자리 모두 빈값으로 초기화
    birthNumberFront.value = '';
    birthNumberBack.value = '';
    isEditMode.value = true;
    
    // 주민등록번호가 등록되어 있으면 자동으로 불러오기
    if (hasBirthNumberRegistered.value) {
      const fetched = await fetchBirthNumberFromServer();
      if (fetched) {
        const fullNumber = birthNumberState.value.value.replace(/[^\d]/g, '');
        if (fullNumber.length === 13) {
          birthNumberFront.value = fullNumber.slice(0, 6);
          birthNumberBack.value = fullNumber.slice(6);
        } else if (fullNumber.length === 7) {
          birthNumberFront.value = '';
          birthNumberBack.value = fullNumber;
        }
      }
    }
  }
};

const cancelEdit = () => {
  isEditMode.value = false;
  birthNumberFront.value = '';
  birthNumberBack.value = '';
  editFormData.value = {
    emp_code: '',
    m_sisul_code: '',
    emp_name: '',
    emp_birth: '',
    emp_birth_number: '',
    emp_job: '',
    emp_in_date: '',
    emp_out_date: '',
    emp_phone: '',
    emp_email: '',
    emp_addr: '',
    emp_addr_detail: ''
  };
};

const saveEdit = async () => {
  if (!isEditFormValid.value) return;
  
  isSaving.value = true;
  
  try {
    // 주민등록번호 조합: 앞자리 + 뒷자리
    const fullBirthNumber = (birthNumberFront.value && birthNumberBack.value) 
      ? (birthNumberFront.value + birthNumberBack.value) 
      : '';
    
    const response = await axios.post('/api/calc/employee', {
      ...editFormData.value,
      emp_birth_number: fullBirthNumber,
      m_sisul_code: user.value?.m_sisul_code
    }, {
      withCredentials: true
    });
    
    if (response.data.success) {
      // 입사일이 변경된 경우, leb_member_info 테이블도 업데이트
      if (selectedEmployee.value && editFormData.value.emp_in_date !== selectedEmployee.value.emp_in_date) {
        try {
          await axios.post('/api/emp/update-start-date', {
            m_sisul_code: user.value?.m_sisul_code,
            emp_name: editFormData.value.emp_name,
            emp_birth: editFormData.value.emp_birth,
            emp_in_date: editFormData.value.emp_in_date
          }, {
            withCredentials: true
          });
        } catch (startDateError) {
          console.warn('급여 테이블 입사일 업데이트 실패:', startDateError);
        }
      }
      
      let message = '사원 정보가 수정되었습니다.';
      
      // 응답에 사원코드가 포함되어 있으면 표시 (신규 생성된 경우)
      if (response.data.empCode) {
        message += ` (사원코드: ${response.data.empCode})`;
      }
      
      store.commit('showSnackbar', {
        text: message,
        color: 'success'
      });
      
      // 사원 목록 새로고침
      await fetchEmployees();
      
      // 수정된 사원 정보로 selectedEmployee 업데이트
      const updatedEmployee = employees.value.find(
        emp => emp.emp_idx === editFormData.value.emp_idx
      );
      if (updatedEmployee) {
        selectedEmployee.value = updatedEmployee;
      }
      
      // 수정 모드 종료 (상세 보기 모드로 전환)
      isEditMode.value = false;
      birthNumberFront.value = '';
      birthNumberBack.value = '';
    } else {
      store.commit('showSnackbar', {
        text: response.data.message || '저장에 실패했습니다.',
        color: 'error'
      });
    }
  } catch (error: any) {
    console.error('사원 저장 오류:', error);
    store.commit('showSnackbar', {
      text: error.response?.data?.message || '사원 저장 중 오류가 발생했습니다.',
      color: 'error'
    });
  } finally {
    isSaving.value = false;
  }
};

const openEmployeeDialog = (employee: Employee | null) => {
  editingEmployee.value = employee;
  employeeDialog.value = true;
};

const handleEmployeeSaved = async () => {
  editingEmployee.value = null;
  await fetchEmployees();
};

const onEditPhoneInput = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const digits = target.value.replace(/[^\d]/g, '').slice(0, 11);
  
  let formatted = digits;
  if (digits.length > 3 && digits.length <= 7) {
    formatted = `${digits.slice(0,3)}-${digits.slice(3)}`;
  } else if (digits.length > 7) {
    formatted = `${digits.slice(0,3)}-${digits.slice(3,7)}-${digits.slice(7)}`;
  }
  
  editFormData.value.emp_phone = formatted;
};

const onEditBirthNumberFrontInput = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const value = target.value.replace(/[^\d]/g, '');
  birthNumberFront.value = value;
};

const onEditBirthNumberBackInput = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const value = target.value.replace(/[^\d]/g, '');
  birthNumberBack.value = value;
};

// 생년월일에서 주민등록번호 앞자리 채우기
const fillBirthFrontFromBirth = () => {
  const front = extractBirthFront(editFormData.value.emp_birth);
  if (front) {
    birthNumberFront.value = front;
  }
};

// 주소 검색 (상세 모달 수정 모드용)
const loadDaumPostcodeScript = async (): Promise<void> => {
  if ((window as any).daum?.Postcode) return;
  if (daumScriptLoading) {
    await daumScriptLoading;
    return;
  }
  daumScriptLoading = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Daum Postcode script load failed'));
    document.head.appendChild(script);
  });
  await daumScriptLoading;
};

const embedDaumPostcode = async () => {
  addressLoading.value = true;
  try {
    await loadDaumPostcodeScript();
    await nextTick();
    const container = postcodeContainer.value;
    if (!container) throw new Error('Container not ready');
    container.innerHTML = '';
    const Postcode = (window as any).daum?.Postcode;
    if (!Postcode) throw new Error('Daum Postcode is undefined');
    const postcode = new Postcode({
      oncomplete: (data: any) => {
        const road = data.roadAddress;
        const jibun = data.jibunAddress;
        const selected = road && road.length > 0 ? road : jibun;
        
        editFormData.value.emp_addr = selected || '';
        
        addressDialog.value = false;
        addressLoading.value = false;
      },
      onresize: (size: any) => {
        try {
          (container as HTMLDivElement).style.height = `${size.height}px`;
        } catch {}
      },
      width: '100%',
      height: '100%'
    });
    try {
      postcode.embed(container, { autoClose: true });
      addressLoading.value = false;
    } catch (e) {
      addressLoading.value = false;
      throw e;
    }
  } catch (e) {
    addressLoading.value = false;
    throw e;
  }
};

const openEditAddressModal = async () => {
  addressDialog.value = true;
};

watch(addressDialog, async (val) => {
  if (val) {
    try {
      await embedDaumPostcode();
    } catch (e) {
      addressDialog.value = false;
      store.commit('showSnackbar', {
        text: '주소 검색 로드에 실패했습니다.',
        color: 'error'
      });
    }
  }
});

watch(detailDialog, (val) => {
  if (!val) {
    resetBirthNumberState();
  }
});

// 사원 정보 업로드
const openMemberUploadDialog = () => {
  memberUploadDialog.value = true;
};

// 일정계획 캘린더 열기
const openScheduleCalendar = (employee: Employee) => {
  selectedEmployeeForSchedule.value = employee;
  scheduleCalendarDialog.value = true;
};

// 일정계획 캘린더에서 직원 변경 시
const handleScheduleEmployeeChange = (employee: Employee) => {
  selectedEmployeeForSchedule.value = employee;
};

// 일정계획 업로드 현황 조회
const fetchScheduleUploadStatus = async () => {
  if (!user.value?.m_sisul_code) return;
  
  try {
    isLoadingScheduleStatus.value = true;
    const response = await axios.get(
      `/api/files/check/${currentYear}/${user.value.m_sisul_code}`,
      { withCredentials: true }
    );
    
    if (response.data.success && response.data.data) {
      // schedule 데이터만 추출
      const statusData: Record<number, boolean> = {};
      for (let m = 1; m <= 12; m++) {
        statusData[m] = response.data.data[m]?.schedule || false;
      }
      scheduleUploadStatus.value = statusData;
    }
  } catch (error) {
    console.error('일정계획 업로드 현황 조회 오류:', error);
  } finally {
    isLoadingScheduleStatus.value = false;
  }
};

// 일정계획 업로드 관련 함수들
const openScheduleUploadDialog = () => {
  scheduleFile.value = null;
  fetchScheduleUploadStatus(); // 모달 열릴 때 현황 조회
  scheduleUploadDialog.value = true;
};

const closeScheduleUploadDialog = () => {
  if (isScheduleUploading.value) return;
  scheduleUploadDialog.value = false;
  scheduleFile.value = null;
  isScheduleDragging.value = false;
};

const triggerScheduleFileInput = () => {
  if (isScheduleUploading.value) return;
  scheduleFileInput.value?.click();
};

const handleScheduleDragOver = () => {
  if (isScheduleUploading.value) return;
  isScheduleDragging.value = true;
};

const handleScheduleDragLeave = () => {
  if (isScheduleUploading.value) return;
  isScheduleDragging.value = false;
};

const handleScheduleDrop = (event: DragEvent) => {
  if (isScheduleUploading.value) return;
  isScheduleDragging.value = false;
  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    selectScheduleFile(files[0]);
  }
};

const handleScheduleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const files = target.files;
  if (files && files.length > 0) {
    selectScheduleFile(files[0]);
  }
  target.value = '';
};

const selectScheduleFile = (file: File) => {
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext !== 'xls' && ext !== 'xlsx') {
    store.commit('showSnackbar', {
      text: '엑셀 파일만 업로드 가능해요',
      color: 'warning'
    });
    return;
  }
  scheduleFile.value = file;
};

const clearScheduleFile = () => {
  scheduleFile.value = null;
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

const uploadScheduleFile = async () => {
  if (!scheduleFile.value || !user.value?.m_sisul_code) return;
  
  try {
    isScheduleUploading.value = true;
    
    const formData = new FormData();
    formData.append('file', scheduleFile.value);
    formData.append('institutionCode', user.value.m_sisul_code);
    formData.append('expectedFileType', '일정계획'); // 일정계획 파일만 허용
    
    const response = await axios.post('/api/files/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      withCredentials: true
    });
    
    if (response.data.success) {
      const fileYM = response.data.fileYearMonth;
      const fileYear = fileYM ? fileYM.substring(0, 4) : '';
      const fileMonth = fileYM ? parseInt(fileYM.substring(4, 6)) : '';
      
      store.commit('showSnackbar', {
        text: `✅ 일정계획 파일이 업로드되었어요! (${fileYear}년 ${fileMonth}월)`,
        color: 'success'
      });
      
      // 현황 갱신 후 모달 닫기
      await fetchScheduleUploadStatus();
      closeScheduleUploadDialog();
    } else {
      store.commit('showSnackbar', {
        text: response.data.message || '업로드에 실패했어요',
        color: 'error'
      });
    }
  } catch (error: any) {
    console.error('일정계획 업로드 오류:', error);
    console.error('응답 데이터:', error.response?.data);
    
    // 디버그 정보가 있으면 콘솔에 출력
    if (error.response?.data?.debug) {
      console.error('디버그 정보:', error.response.data.debug);
    }
    
    store.commit('showSnackbar', {
      text: error.response?.data?.message || '업로드 중 오류가 발생했어요',
      color: 'error'
    });
  } finally {
    isScheduleUploading.value = false;
  }
};

// 사원 정보 업로드 완료 핸들러
const handleMemberUploaded = async () => {
  // 업로드 완료 후 사원 목록 재조회
  await fetchEmployees();
};

// 사원 삭제 확인 모달 열기
const confirmDeleteEmployee = (employee: Employee | null) => {
  if (!employee) {
    store.commit('showSnackbar', {
      text: '삭제할 사원 정보가 없습니다.',
      color: 'error'
    });
    return;
  }
  employeeToDelete.value = employee;
  deleteDialog.value = true;
};

// 사원 삭제 실행
const deleteEmployee = async () => {
  if (!employeeToDelete.value?.emp_idx) {
    store.commit('showSnackbar', {
      text: '삭제할 사원 정보가 없습니다.',
      color: 'error'
    });
    return;
  }

  try {
    isDeleting.value = true;
    
    const response = await axios.delete(`/api/emp/facility/${employeeToDelete.value.emp_idx}`, {
      withCredentials: true
    });

    if (response.data.success) {
      store.commit('showSnackbar', {
        text: response.data.message || '사원이 삭제되었습니다.',
        color: 'success'
      });
      deleteDialog.value = false;
      detailDialog.value = false;
      employeeToDelete.value = null;
      selectedEmployee.value = null;
      await fetchEmployees();
    } else {
      store.commit('showSnackbar', {
        text: response.data.message || '사원 삭제에 실패했습니다.',
        color: 'error'
      });
    }
  } catch (error: any) {
    console.error('사원 삭제 오류:', error);
    store.commit('showSnackbar', {
      text: error.response?.data?.message || '사원 삭제 중 오류가 발생했습니다.',
      color: 'error'
    });
  } finally {
    isDeleting.value = false;
  }
};

// 컴포넌트 마운트
onMounted(async () => {
  try {
    await store.dispatch('validateAuth');
    
    // 사용자 정보 확인
    if (!user.value?.m_sisul_code) {
      console.error('사용자 정보가 없습니다:', user.value);
      store.commit('showSnackbar', {
        text: '사용자 정보를 불러올 수 없습니다. 다시 로그인해주세요.',
        color: 'error'
      });
      return;
    }
    
    await fetchEmployees();
  } catch (error) {
    console.error('컴포넌트 마운트 중 오류:', error);
    store.commit('showSnackbar', {
      text: '페이지 초기화 중 오류가 발생했습니다.',
      color: 'error'
    });
  }
});
</script>

<style scoped lang="scss">
@import '@/assets/styles/pgmEmployees.scss';
</style>

